const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const getApiBase = (): string => {
  return import.meta.env.VITE_API_URL || '';
};

const MAX_IMAGES = 25;
const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB

export const validateImageUpload = (files: File[]): string | null => {
  if (files.length > MAX_IMAGES) {
    return `En fazla ${MAX_IMAGES} görsel yükleyebilirsiniz.`;
  }

  for (const file of files) {
    if (!file.type.startsWith('image/')) {
      return 'Sadece görsel dosyaları yükleyebilirsiniz.';
    }
    if (file.size > MAX_FILE_SIZE) {
      return 'Her bir görsel en fazla 30MB olabilir.';
    }
  }

  return null;
};

export const uploadImageToS3 = async (file: File, folder: string): Promise<string> => {
  try {
    console.log(`Resim yükleniyor: ${file.name} -> ${folder}`);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('folder', folder);

    const res = await fetch(`${getApiBase()}/api/upload`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || 'Dosya yükleme başarısız');
    }

    const data = await res.json();
    console.log('Dosya başarıyla yüklendi:', data.url);
    return data.url;
  } catch (error) {
    console.error('S3 yükleme hatası:', error);
    throw error;
  }
};

export const uploadVideoToS3 = async (videoFile: File, folder: string): Promise<string> => {
  try {
    console.log(`Video yükleniyor: ${videoFile.name} -> ${folder}`);

    // Backend'den pre-signed URL al
    const res = await fetch(`${getApiBase()}/api/upload/video-url`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        folder,
        fileName: videoFile.name,
        contentType: videoFile.type,
      }),
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Failed to get upload URL' }));
      throw new Error(error.error || 'Video upload URL alınamadı');
    }

    const { uploadUrl, fileUrl } = await res.json();

    // Pre-signed URL ile direkt S3'e yükle
    const uploadRes = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': videoFile.type },
      body: videoFile,
    });

    if (!uploadRes.ok) {
      throw new Error('Video S3\'e yüklenemedi');
    }

    console.log('Video başarıyla yüklendi:', fileUrl);
    return fileUrl;
  } catch (error) {
    console.error('S3 video yükleme hatası:', error);
    throw error;
  }
};

// Video URL'sinden kapak (poster) URL'si türet: query/hash atılır, son uzantı -> .poster.webp
export const getVideoPosterUrl = (videoUrl: string): string => {
  const clean = videoUrl.split(/[?#]/)[0];
  const slash = clean.lastIndexOf('/');
  const dot = clean.lastIndexOf('.');
  const base = dot > slash + 1 ? clean.slice(0, dot) : clean;
  return `${base}.poster.webp`;
};

export const generateVideoThumbnail = async (videoUrl: string): Promise<string | null> => {
  try {
    const res = await fetch(`${getApiBase()}/api/upload/video-thumbnail`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ videoUrl }),
    });

    if (!res.ok) {
      console.error('Kapak görseli oluşturma hatası:', await res.text());
      return null;
    }

    const { thumbnailUrl } = await res.json();
    return thumbnailUrl || null;
  } catch (error) {
    console.error('Kapak görseli oluşturma hatası:', error);
    return null;
  }
};

export const deleteFileFromS3 = async (fileUrl: string): Promise<void> => {
  try {
    if (!fileUrl || typeof fileUrl !== 'string') {
      console.error('Geçersiz fileUrl değeri:', fileUrl);
      return;
    }

    const res = await fetch(`${getApiBase()}/api/files`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fileUrl }),
    });

    if (!res.ok) {
      console.error('Dosya silme hatası:', await res.text());
    } else {
      console.log('Dosya başarıyla silindi:', fileUrl);
    }
  } catch (error) {
    console.error('S3 dosya silme hatası:', error);
  }
};

export const deleteProjectFilesFromS3 = async (_projectId: string, filePaths: string[]): Promise<void> => {
  try {
    if (!filePaths || filePaths.length === 0) return;

    const res = await fetch(`${getApiBase()}/api/files`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ fileUrls: filePaths }),
    });

    if (!res.ok) {
      console.error('Toplu dosya silme hatası:', await res.text());
    }
  } catch (error) {
    console.error('Proje dosyaları silinirken hata:', error);
  }
};
